import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { CreateOrderRequest, OrderingState } from "@toema/shared-types";
import { createOrder, isOrdersPaused } from "../api.js";
import type { Cart } from "../lib/cart.js";
import { isPaused } from "../lib/ordering.js";
import PausedPill from "./PausedPill.js";
import "./OrderForm.css";

type Fulfilment = "pickup" | "delivery";

type Submission =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; reference: string }
  | { kind: "failed"; message: string };

interface OrderFormProps {
  cart: Cart;
  ordering: OrderingState | null;
  timezone: string | null;
  onPlaced: (ticketNumber: number | null) => void;
  onRefused: () => void;
}

const STOPPED_MEANWHILE = "The shop stopped taking orders a moment ago. Your basket is still here.";

/**
 * What the customer is told to quote.
 *
 * The server issues a ticket number per store per day when the order is
 * written, so it is short enough to say at a counter. The UUID tail is the
 * fallback for an order placed against a backend old enough not to number
 * them.
 */
function reference(order: { ticketNumber: number | null; id: string }): string {
  if (order.ticketNumber !== null) return `#${String(order.ticketNumber).padStart(3, "0")}`;
  const tail = order.id.split("-").at(-1) ?? order.id;
  return tail.slice(-6).toUpperCase();
}

export default function OrderForm({ cart, ordering, timezone, onPlaced, onRefused }: OrderFormProps) {
  const [fulfilment, setFulfilment] = useState<Fulfilment>("pickup");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [submission, setSubmission] = useState<Submission>({ kind: "idle" });

  const delivery = fulfilment === "delivery";
  const sending = submission.kind === "sending";
  const paused = isPaused(ordering);
  const canSubmit = cart.lines.length > 0 && !sending && !paused;

  useEffect(() => {
    if (paused) setSubmission((current) => (current.kind === "failed" ? { kind: "idle" } : current));
  }, [paused]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (cart.lines.length === 0) return;

    setSubmission({ kind: "sending" });

    const body: CreateOrderRequest = {
      source: "landing",
      customerName: name.trim(),
      customerPhone: phone.trim(),
      // Only a delivery carries an address; a pickup deliberately sends null
      // rather than a stale string left in the field before switching.
      customerAddress: delivery ? address.trim() : null,
      homeDelivery: delivery,
      // Kitchen notes are deliberately not in v1 — see the design reference.
      notes: null,
      items: cart.lines.map((line) => ({
        menuItemId: line.menuItemId,
        quantity: line.quantity,
        modifiers: {},
      })),
    };

    try {
      const order = await createOrder(body);
      setSubmission({ kind: "sent", reference: reference(order) });
      onPlaced(order.ticketNumber);
      cart.clear();
    } catch (error) {
      if (isOrdersPaused(error)) {
        setSubmission({ kind: "failed", message: STOPPED_MEANWHILE });
        onRefused();
        return;
      }
      setSubmission({
        kind: "failed",
        message:
          error instanceof Error
            ? error.message
            : "The order could not be sent. Please try again.",
      });
    }
  };

  return (
    <form className="order" onSubmit={handleSubmit} noValidate={false}>
      <h2 className="order__title">Where is it going?</h2>

      {/* A real pressed-state pair rather than radios dressed as buttons:
          each is a button that reports whether it is the current choice. */}
      <div className="segmented" role="group" aria-label="Pickup or delivery">
        <button
          type="button"
          className="segmented__opt"
          aria-pressed={!delivery}
          onClick={() => setFulfilment("pickup")}
        >
          Pickup
        </button>
        <button
          type="button"
          className="segmented__opt"
          aria-pressed={delivery}
          onClick={() => setFulfilment("delivery")}
        >
          Delivery
        </button>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="order-name">
          Name
        </label>
        <input
          className="field__input"
          id="order-name"
          name="name"
          type="text"
          autoComplete="name"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="order-phone">
          Phone
        </label>
        <input
          className="field__input"
          id="order-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          required
          maxLength={32}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>

      {delivery && (
        <div className="field">
          <label className="field__label" htmlFor="order-address">
            Address
          </label>
          <input
            className="field__input"
            id="order-address"
            name="address"
            type="text"
            autoComplete="street-address"
            required
            maxLength={500}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
      )}

      {paused && ordering && (
        <PausedPill ordering={ordering} timezone={timezone} className="order__paused" />
      )}

      <button className="order__send" type="submit" disabled={!canSubmit}>
        {sending ? "Sending…" : "Send the order"}
      </button>

      {!paused && cart.lines.length === 0 && submission.kind !== "sent" && (
        <p className="order__hint">Add something from the menu first.</p>
      )}

      {/* Polite: the customer is looking at the button they just pressed. */}
      <p className="order__status" role="status">
        {submission.kind === "sent"
          ? `Order received — ticket ${submission.reference}. The grill has it.`
          : ""}
      </p>

      {submission.kind === "failed" && (
        <p className="order__error" role="alert">
          {submission.message}
        </p>
      )}
    </form>
  );
}
